import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';

interface Benefit {
  /** i18n key suffix under pages.about.benefits.cards. */
  key: string;
  icon: ReactNode;
  iconBg: string;
  accent: string;
}

const BENEFITS: Benefit[] = [
  {
    key: 'remember',
    accent: '#FDE47A',
    iconBg: 'rgba(247,203,45,0.13)',
    icon: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="4" />
        <path d="M12 7v10M7 12h10" />
      </>
    ),
  },
  {
    key: 'organise',
    accent: '#7fa8ff',
    iconBg: 'rgba(127,168,255,0.13)',
    icon: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1.6" />
        <rect x="14" y="3" width="7" height="7" rx="1.6" />
        <rect x="3" y="14" width="7" height="7" rx="1.6" />
        <rect x="14" y="14" width="7" height="7" rx="1.6" />
      </>
    ),
  },
  {
    key: 'compound',
    accent: '#f5a3c2',
    iconBg: 'rgba(245,163,194,0.14)',
    icon: (
      <>
        <path d="M3 17l5-5 4 4 8-8" />
        <path d="M21 8v5M21 8h-5" />
      </>
    ),
  },
];

export const Section22 = () => {
  const t = useTranslations();
  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.head}>
          <div className={styles.eyebrow}>
            {t('pages.about.benefits.eyebrow')}
          </div>
          <h2 className={styles.title}>
            {t.rich('pages.about.benefits.title', {
              highlight: (chunks) => (
                <span className={styles.highlight}>{chunks}</span>
              ),
            })}
          </h2>
          <p className={styles.lead}>{t('pages.about.benefits.lead')}</p>
        </div>

        <div className={styles.grid}>
          {BENEFITS.map((benefit) => (
            <div key={benefit.key} className={styles.card}>
              <span
                className={styles.icon}
                style={{ background: benefit.iconBg, color: benefit.accent }}
              >
                <svg viewBox="0 0 24 24" className={styles.glyph}>
                  {benefit.icon}
                </svg>
              </span>
              <h3 className={styles.cardTitle}>
                {t(`pages.about.benefits.cards.${benefit.key}.title`)}
              </h3>
              <p className={styles.body}>
                {t(`pages.about.benefits.cards.${benefit.key}.body`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: 'marketing-dark:bg-mf-navy pt-[74px] pb-5 text-center',
  wrap: 'mx-auto max-w-[1200px] px-6 lg:px-10',
  head: 'mx-auto max-w-[700px]',
  eyebrow:
    'mb-4 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  title:
    'font-inter text-3xl font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-[40px]',
  highlight: 'text-mf-blue marketing-dark:text-mf-coral',
  lead: 'mx-auto mt-[18px] max-w-[560px] text-lg leading-[1.65] text-mf-gray-600 marketing-dark:text-white/66',
  grid: 'mt-[46px] grid grid-cols-1 gap-[22px] text-left lg:grid-cols-3',
  card: [
    'rounded-[18px] border px-7 py-8',
    'border-[#F0F0F0] bg-white',
    'marketing-dark:border-white/10 marketing-dark:bg-white/[0.03]',
  ].join(' '),
  icon: 'mb-[18px] flex h-[52px] w-[52px] items-center justify-center rounded-[15px]',
  glyph: 'h-[25px] w-[25px] fill-none stroke-current stroke-[1.7]',
  cardTitle:
    'mb-2.5 font-inter text-xl font-bold text-mf-off-black-600 marketing-dark:text-white',
  body: 'text-[15px] leading-[1.65] text-mf-gray-600 marketing-dark:text-white/62',
};
